import { useState } from "react";
import { format, formatDistanceToNow, isToday, isTomorrow, isThisWeek } from "date-fns";
import { AlertTriangle, Calendar, ChevronDown, ChevronRight } from "lucide-react";
import { useSafeNavigate } from "../../../hooks/useSafeNavigate";
import { formatZonedTime, getTimeZoneAbbr, toDisplayDate } from "../../../lib/time";
import { enumerateOverlappedDays, localDayKey } from "../../../lib/days";

function groupSessionsByDate(sessions, { recentFirst = false } = {}) {
  const groups = {};

  sessions.forEach((session) => {
    const sourceDate = session.winningSlot?.start
      ? new Date(session.winningSlot.start)
      : session.firstSlot?.start
        ? new Date(session.firstSlot.start)
        : null;

    if (!sourceDate) return;

    const displayDate = toDisplayDate(sourceDate, session.displayTimeZone) || sourceDate;
    const dateKey = localDayKey(displayDate);
    if (!groups[dateKey]) {
      groups[dateKey] = {
        date: displayDate,
        sortDate: sourceDate,
        sessions: [],
      };
    }
    groups[dateKey].sessions.push({
      ...session,
      sortDate: sourceDate,
      displayDate,
    });
  });

  // Sort sessions within each group by time
  Object.values(groups).forEach((group) => {
    group.sessions.sort((a, b) => a.sortDate - b.sortDate);
  });

  // Sort groups by date (chronological, or most-recent-first for past).
  const sorted = Object.values(groups).sort((a, b) => a.sortDate - b.sortDate);
  return recentFirst ? sorted.reverse() : sorted;
}

function getDateLabel(displayDate, rawDate, timeZone, showTimeZone) {
  let baseLabel = null;
  if (isToday(displayDate)) baseLabel = "Today";
  if (!baseLabel && isTomorrow(displayDate)) baseLabel = "Tomorrow";
  if (!baseLabel && isThisWeek(displayDate)) baseLabel = format(displayDate, "EEEE");
  if (!baseLabel) baseLabel = format(displayDate, "EEEE, MMM d");
  const shouldShow = showTimeZone !== false;
  const abbr = shouldShow ? getTimeZoneAbbr(rawDate || displayDate, timeZone) : "";
  return abbr ? `${baseLabel} ${abbr}` : baseLabel;
}

function AgendaItem({ session, groupColor, showVoteNeeded, conflictsWith = [] }) {
  const safeNavigate = useSafeNavigate();
  const date = session.winningSlot?.start
    ? new Date(session.winningSlot.start)
    : session.firstSlot?.start
      ? new Date(session.firstSlot.start)
      : null;

  // A finalized session that runs past local midnight gets a compact "Ends …"
  // tail hint on its start-day row, rather than being duplicated under the next
  // day (a 1 AM continuation row reads as a separate game). Only finalized
  // sessions qualify — an OPEN poll's firstSlot end is a proxy, not a booking.
  const endDate = session.winningSlot?.end
    ? new Date(session.winningSlot.end)
    : null;
  const crossesMidnight =
    date &&
    endDate &&
    enumerateOverlappedDays({
      start: date,
      end: endDate,
      timeZone: session.displayTimeZone,
    }).length > 1;

  const handleOpen = () => {
    const target = `/scheduler/${session.id}`;
    safeNavigate(target);
  };

  const hasConflict = conflictsWith.length > 0;

  return (
    <button
      type="button"
      onClick={handleOpen}
      className="flex w-full items-center gap-3 rounded-xl border border-slate-200/70 bg-white px-3 py-2 text-left transition-colors hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:hover:bg-slate-700"
      style={{
        borderLeftWidth: groupColor ? "4px" : undefined,
        borderLeftColor: groupColor || undefined,
      }}
    >
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium text-slate-900 dark:text-slate-100 truncate ${session.status === "CANCELLED" ? "line-through opacity-60" : ""}`}>
          {session.title || "Untitled"}
        </p>
        <div className="mt-0.5 flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
          {date && (
            <span>
              {formatZonedTime(date, session.displayTimeZone, "h:mm a", {
                showTimeZone: session.showTimeZone,
              })}
            </span>
          )}
          {session.status === "CANCELLED" && (
            <span className="rounded-full bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold text-rose-700 dark:bg-rose-900/50 dark:text-rose-300">
              Cancelled
            </span>
          )}
          {session.status !== "CANCELLED" && session.calendarSync?.state === "RESCHEDULED" && (
            <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 dark:bg-amber-900/50 dark:text-amber-300">
              Rescheduled
            </span>
          )}
          {session.status === "OPEN" && showVoteNeeded && (
            <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 dark:bg-amber-900/50 dark:text-amber-300">
              Vote needed
            </span>
          )}
          {session.status === "OPEN" && !showVoteNeeded && (
            <span className="rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700 dark:bg-blue-900/50 dark:text-blue-300">
              Open
            </span>
          )}
        </div>
        {crossesMidnight && (
          <p className="mt-0.5 text-xs text-amber-700 dark:text-amber-400">
            Ends{" "}
            {formatZonedTime(endDate, session.displayTimeZone, "EEE h:mm a", {
              showTimeZone: session.showTimeZone,
            })}
          </p>
        )}
        {hasConflict && (
          <div
            className="mt-1 flex items-center gap-1 text-xs font-medium text-amber-700 dark:text-amber-400"
            title={`Conflicts with: ${conflictsWith.join(", ")}`}
          >
            <AlertTriangle className="h-3 w-3 flex-shrink-0" />
            {/* Compact badge only — the full conflicting-titles list lives in the
                `title` tooltip (mirrors the desktop calendar tile's bare
                "Conflict"). Rendering the joined list inline produced O(N²)
                wrapping text on overlap-heavy days, which kept the mobile
                dashboard layout from settling (buttons became un-clickable). */}
            <span>
              {conflictsWith.length === 1
                ? "Schedule conflict"
                : `Schedule conflict (${conflictsWith.length})`}
            </span>
          </div>
        )}
      </div>
      <ChevronRight className="h-4 w-4 flex-shrink-0 text-slate-400" />
    </button>
  );
}

function AgendaGroups({ groups, getGroupColor, needsVote, conflictsById }) {
  return (
    <div className="space-y-4">
      {groups.map((group) => (
        <div key={localDayKey(group.date)}>
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              {getDateLabel(
                group.date,
                group.sortDate,
                group.sessions?.[0]?.displayTimeZone,
                group.sessions?.[0]?.showTimeZone
              )}
            </p>
            <p className="text-xs text-slate-400 dark:text-slate-500">
              {formatDistanceToNow(group.sortDate, { addSuffix: true })}
            </p>
          </div>
          <div className="space-y-2">
            {group.sessions.map((session) => (
              <AgendaItem
                key={session.id}
                session={session}
                groupColor={
                  session.questingGroupId
                    ? getGroupColor(session.questingGroupId)
                    : null
                }
                showVoteNeeded={needsVote.has(session.id)}
                conflictsWith={conflictsById.get(session.id) || []}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function MobileAgendaView({
  sessions = [],
  pastSessions = [],
  getGroupColor = () => null,
  needsVote = new Set(),
  conflictsById = new Map(),
}) {
  const [showPast, setShowPast] = useState(false);
  const groupedSessions = groupSessionsByDate(sessions);
  // Past finalized sessions, most-recent-first, gated behind a tap so a phone
  // user can still reach a completed game (desktop's calendar shows these; the
  // mobile agenda previously dropped them entirely — a data-parity defect).
  const groupedPast = groupSessionsByDate(pastSessions, { recentFirst: true });
  const hasUpcoming = groupedSessions.length > 0;
  const hasPast = groupedPast.length > 0;
  // Count what will actually render (groupSessionsByDate drops dateless
  // sessions), not the raw prop length — otherwise the disclosure label can
  // promise more rows than it shows.
  const pastCount = groupedPast.reduce((n, g) => n + g.sessions.length, 0);

  if (!hasUpcoming && !hasPast) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <Calendar className="h-10 w-10 text-slate-300 dark:text-slate-600" />
        <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
          No upcoming sessions
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {hasUpcoming ? (
        <AgendaGroups
          groups={groupedSessions}
          getGroupColor={getGroupColor}
          needsVote={needsVote}
          conflictsById={conflictsById}
        />
      ) : (
        <div className="flex flex-col items-center justify-center py-6 text-center">
          <Calendar className="h-8 w-8 text-slate-300 dark:text-slate-600" />
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
            No upcoming sessions
          </p>
        </div>
      )}

      {hasPast && (
        <div className="border-t border-slate-200/70 pt-3 dark:border-slate-700">
          <button
            type="button"
            onClick={() => setShowPast((prev) => !prev)}
            aria-expanded={showPast}
            data-testid="mobile-agenda-past-toggle"
            className="flex min-h-[44px] w-full items-center justify-between rounded-xl px-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 dark:text-slate-400 dark:hover:bg-slate-800"
          >
            <span>
              Past sessions ({pastCount})
            </span>
            <ChevronDown
              className={`h-4 w-4 flex-shrink-0 motion-safe:transition-transform ${showPast ? "rotate-180" : ""}`}
            />
          </button>
          {showPast && (
            <div className="mt-3" data-testid="mobile-agenda-past-list">
              {/* conflictsById is computed over UPCOMING finalized only (a
                  historical double-book is moot), so past rows render without a
                  conflict warning by design — matching the desktop session
                  list. Passed through for shape symmetry only. */}
              <AgendaGroups
                groups={groupedPast}
                getGroupColor={getGroupColor}
                needsVote={needsVote}
                conflictsById={conflictsById}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
